import { Action, ResourceType } from '../../../../typings/permissions';
import { window } from '../../../globals';
import { SBOM_PORTAL, SBOM_PORTAL_PRIVATE } from '../../../../shared/featureFlags/flagNames';
import { isFeatureEnabled } from '../../../helpers/features';

/**
 * Permissions helpers
 */
const canViewGeneralSettings = (): boolean => window.hasPermission({
  resourceType: ResourceType.OrganizationGeneralSettings,
  action: Action.View,
});

const canViewPortalSettings = (): boolean => isFeatureEnabled(SBOM_PORTAL) && window.hasPermission({
  resourceType: ResourceType.OrganizationPortalSettings,
  action: Action.View,
});

const canViewPortalTokenSettings = (): boolean =>
  isFeatureEnabled(SBOM_PORTAL) &&
  isFeatureEnabled(SBOM_PORTAL_PRIVATE) &&
  canViewPortalSettings() &&
  window.hasPermission({
    resourceType: ResourceType.OrganizationPortalReports,
    action: Action.Manage,
  });

const canEditAuthSettings = (): boolean => window.hasPermission({
  resourceType: ResourceType.OrganizationAuthSettings,
  action: Action.Edit,
});

const canEditIntegrationSettings = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.OrganizationIntegrationSettings,
});

const canViewAnyUser = (): boolean => window.hasPermission({
  resourceType: ResourceType.UserAny,
  action: Action.View,
});

const canEditLanguageSettings = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.OrganizationLanguageSettings,
});

const canEditBillingSettings = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.OrganizationBillingSettings,
});

const canViewNotificationBanners = () => window.on_prem &&
  window.hasPermission({
    resourceType: ResourceType.OrganizationGeneralSettings,
    action: Action.Edit,
  });

const canViewAuditLogs = (): boolean => window.hasPermission({
  action: Action.View,
  resourceType: ResourceType.OrganizationAudit,
});

export const canSetDefaultLicensingPolicy = (): boolean => window.hasPermission({
  action: Action.Set,
  resourceType: ResourceType.OrganizationDefaultLicensingPolicy,
});

export const canSetDefaultSecurityPolicy = (): boolean => window.hasPermission({
  action: Action.Set,
  resourceType: ResourceType.OrganizationDefaultSecurityPolicy,
});

export const canSetDefaultQualityPolicy = (): boolean => window.hasPermission({
  action: Action.Set,
  resourceType: ResourceType.OrganizationDefaultSecurityPolicy,
});

export const canEditDefaultLicensingFilter = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.ProjectIssueFilterLicensingAny,
});

export const canEditDefaultSecurityFilter = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.ProjectIssueFilterSecurityAny,
});

export const canEditDefaultQualityFilter = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.ProjectIssueFilterQualityAny,
});

/**
 * Projects Settings
 */
const canViewProjectIssueSettings = (): boolean => [
  canSetDefaultLicensingPolicy(),
  canSetDefaultSecurityPolicy(),
  canSetDefaultQualityPolicy(),
  canEditDefaultLicensingFilter(),
  canEditDefaultSecurityFilter(),
  canEditDefaultQualityFilter(),
].some(Boolean);

export const shouldDisplayLanguageSettings = canEditLanguageSettings;
export const shouldDisplayBillingSettings = canEditBillingSettings;
export const shouldDisplayAuditLogs = canViewAuditLogs;
export const shouldDisplayOrganizationGeneralSettings = () => canViewGeneralSettings() && canEditAuthSettings();
export const shouldDisplayOrganizationPortalSettings = canViewPortalSettings;
export const shouldDisplayUserSettings = canViewAnyUser;
export const shouldDisplayNotificationBanners = canViewNotificationBanners;
export const shouldDisplayProjectSettings = canViewGeneralSettings;
export const shouldDisplayProjectGeneralSettings = canViewGeneralSettings;
export const shouldDisplayProjectIssueSettings = canViewProjectIssueSettings;
export const shouldDisplayProjectNotificationSettings = canViewGeneralSettings;
export const shouldDisplayProjectUpdateHookSettings = canViewGeneralSettings;
export const shouldDisplayAuthenticationSettings = canEditAuthSettings;
export const shouldDisplayJiraSettings = canEditIntegrationSettings;
export const shouldDisplaySlackSettings = canEditIntegrationSettings;
export const shouldDisplayCustomLicenseScanSettings = canEditIntegrationSettings;
export const shouldDisplayOrganizationPortalTokenSettings = canViewPortalTokenSettings;
